import { name, ScreenInInitCallbackSymbol } from '../symbols'
import { named, withDependencies } from '@wix/thunderbolt-ioc'
import {
	IPageWillMountHandler,
	ICompsLifeCycle,
	IPageWillUnmountHandler,
	CompsLifeCycleSym,
	DSViewModeAPISymbol,
	EditorFeatureConfigSymbol,
	FeatureStateSymbol,
	PageFeatureConfigSymbol,
	pageIdSym,
	IViewMode,
} from '@wix/thunderbolt-symbols'

import type { Actions, GetScreenInInitCallback, ScreenInEditorConfig, ScreenInFeatureState } from '../types'
import { IFeatureState } from 'thunderbolt-feature-state'
import { EditorNavigationSymbol, IEditorNavigation } from 'ds-feature-editor-navigation'

const SCREEN_IN_DS_CALLBACK = 'ScreenInDSCallback'

const editorScreenInFactory = (
	featureConfig: { compIdToActions: Actions },
	editorConfig: ScreenInEditorConfig,
	featureState: IFeatureState<ScreenInFeatureState>,
	pageId: string,
	navigation: IEditorNavigation,
	getScreenInInitCallback: GetScreenInInitCallback,
	compsLifeCycle: ICompsLifeCycle,
	viewModeApi: IViewMode
): IPageWillMountHandler & IPageWillUnmountHandler => {
	let shouldInitScreenIn = editorConfig.shouldEnableScreenIn
	let unregisterFromCompLifeCycle = () => {}
	const compIds = Object.keys(featureConfig.compIdToActions || {})
	function destroy() {
		const state = featureState.get()

		if (state) {
			state.screenInManager.clearState()
		}
	}

	function toggleScreenIn() {
		if (shouldInitScreenIn) {
			const isDuringViewModeSwitch = viewModeApi.isDuringViewModeSwitch()
			const initCallback = getScreenInInitCallback()

			// tap into lifecycle (mount) hook to get and store the displayedId - support for Repeaters
			unregisterFromCompLifeCycle = compsLifeCycle.registerToCompLifeCycle(
				compIds,
				SCREEN_IN_DS_CALLBACK,
				(compId: string, displayedId: string, element: HTMLElement) => {
					if (isDuringViewModeSwitch) {
						destroy()
					}

					if (!initCallback) {
						return
					}

					navigation.getNextNavigationIntent().then(() => {
						initCallback(compId, displayedId, element)
					})
				}
			)
		} else {
			destroy()
		}
	}

	return {
		name: 'editorScreenInFactory',
		pageWillMount() {
			// master page components with screen in are triggered as part of edit to preview mode navigation
			shouldInitScreenIn = editorConfig.shouldEnableScreenIn && pageId !== 'masterPage'
			toggleScreenIn()
		},
		pageWillUnmount() {
			if (compIds.length && shouldInitScreenIn) {
				unregisterFromCompLifeCycle()
			}
		},
	}
}

export const ScreenIn = withDependencies(
	[
		named(PageFeatureConfigSymbol, name),
		named(EditorFeatureConfigSymbol, name),
		named(FeatureStateSymbol, name),
		pageIdSym,
		EditorNavigationSymbol,
		ScreenInInitCallbackSymbol,
		CompsLifeCycleSym,
		DSViewModeAPISymbol,
	],
	editorScreenInFactory
)
